import { useEffect, useMemo, useState } from 'react';
import { Check, Copy, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CRYPTO_CURRENCY_OPTIONS } from './CryptoCurrencyPicker';

function formatTime(ms) {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}

function getMethodLabel(option, t) {
  if (!option) return '';
  const network = t(`plans.cryptoPicker.networks.${option.networkKey}`);
  const normalizedNetwork = network.replace(/\s+network$/i, '');
  if (!normalizedNetwork || normalizedNetwork.toUpperCase() === option.symbol) {
    return option.symbol;
  }
  return `${option.symbol} ${normalizedNetwork}`;
}

function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value || ''));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="flex h-11 shrink-0 items-center justify-center gap-2 rounded-home-16 bg-glass-16 px-4 font-sans font-bold text-[12px] leading-[14px] text-text-primary"
    >
      {copied ? <Check size={17} /> : <Copy size={17} />}
      {copied ? label.copied : label.copy}
    </button>
  );
}

export default function CryptoPaymentDetails({ payment, onBack }) {
  const { t } = useTranslation();
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const isSbp = payment?.cryptoCurrency === 'sbp';
  const option = isSbp
    ? { mark: null, accent: '#0A8F68', icon: '/icons/payment/sbp.svg' }
    : CRYPTO_CURRENCY_OPTIONS.find((item) => item.id === payment?.cryptoCurrency);
  const cryptoMethodLabel = isSbp ? '' : getMethodLabel(option, t);
  const amountTicker = isSbp ? payment.currency : (option?.symbol || payment.currency);
  const expiresAt = payment?.expires_at ? new Date(payment.expires_at).getTime() : now;
  const qrData = payment?.address || '';
  const qrUrl = useMemo(() => {
    if (payment?.qr_code_image) return payment.qr_code_image;
    const data = encodeURIComponent(qrData);
    return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=12&data=${data}`;
  }, [payment?.qr_code_image, qrData]);

  if (!payment) return null;

  return (
    <section className="px-4 pb-6">
      <div className="rounded-home-24 border border-border-default bg-glass-8 p-4">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="font-sans font-bold text-[18px] leading-[21px] text-text-primary">
              {isSbp
                ? t('plans.sbpPayment.title')
                : t('plans.cryptoPayment.title', { method: cryptoMethodLabel })}
            </p>
            <p className="mt-1 font-sans font-semibold text-[11px] leading-[13px] text-text-secondary">
              {t(isSbp ? 'plans.sbpPayment.subtitle' : 'plans.cryptoPayment.subtitle')}
            </p>
          </div>
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full font-sans font-bold text-[22px] leading-none text-base-white"
            style={{ backgroundColor: option?.accent || '#50AF95' }}
            aria-hidden="true"
          >
            {option?.icon ? (
              <img src={option.icon} alt="" className="h-7 w-7" />
            ) : (
              option?.mark || payment.currency
            )}
          </span>
        </div>

        <div className="rounded-home-20 bg-base-white p-3">
          <img
            src={qrUrl}
            alt={t(isSbp ? 'plans.sbpPayment.qrAlt' : 'plans.cryptoPayment.qrAlt')}
            className="mx-auto h-[220px] w-[220px]"
          />
        </div>

        {payment.address && (
          <div className="mt-4 rounded-home-16 border border-accent-orange-start/40 bg-glass-8 p-3">
            <p className="mb-2 font-sans font-semibold text-[11px] leading-[13px] text-text-secondary">
              {t(isSbp ? 'plans.sbpPayment.address' : 'plans.cryptoPayment.address', { currency: cryptoMethodLabel })}
            </p>
            <div className="flex items-center gap-2">
              <div className="min-h-[44px] flex-1 rounded-home-16 bg-glass-8 px-3 py-3 font-sans font-semibold text-[12px] leading-[16px] text-text-primary break-all">
                {payment.address}
              </div>
              <CopyButton value={payment.address} label={{ copy: t('common.copy'), copied: t('common.copied') }} />
            </div>
          </div>
        )}

        <div className="mt-3">
          <p className="mb-2 font-sans font-semibold text-[11px] leading-[13px] text-text-secondary">
            {t('plans.cryptoPayment.exactAmount')}
          </p>
          <div className="flex items-center gap-2">
            <div className="min-h-[44px] flex-1 rounded-home-16 border border-border-default bg-glass-8 px-3 py-3 font-sans font-extrabold text-[22px] leading-[26px] text-text-primary break-all">
              {payment.amount} {amountTicker}
            </div>
            <CopyButton value={payment.amount} label={{ copy: t('common.copy'), copied: t('common.copied') }} />
          </div>
        </div>

        {payment.memo && (
          <div className="mt-3">
            <p className="mb-2 font-sans font-semibold text-[11px] leading-[13px] text-text-secondary">
              {t('plans.cryptoPayment.memo')}
            </p>
            <div className="flex items-center gap-2">
              <div className="min-h-[44px] flex-1 rounded-home-16 border border-border-default bg-glass-8 px-3 py-3 font-sans font-semibold text-[12px] leading-[16px] text-text-primary break-all">
                {payment.memo}
              </div>
              <CopyButton value={payment.memo} label={{ copy: t('common.copy'), copied: t('common.copied') }} />
            </div>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between gap-3 rounded-home-16 bg-glass-16 px-4 py-3">
          <div className="flex items-center gap-2 font-sans font-semibold text-[12px] leading-[14px] text-text-secondary">
            <Clock size={18} />
            {t('plans.cryptoPayment.timeLeft')}
          </div>
          <p className="font-sans font-extrabold text-[22px] leading-[26px] text-accent-orange-start">
            {formatTime(expiresAt - now)}
          </p>
        </div>

        <p className="mt-4 font-sans font-semibold text-[11px] leading-[15px] text-text-secondary">
          {t(isSbp ? 'plans.sbpPayment.warning' : 'plans.cryptoPayment.warning')}
        </p>

        <button
          type="button"
          onClick={onBack}
          className="mt-4 h-11 w-full rounded-home-16 bg-glass-16 font-sans font-bold text-[13px] leading-[15px] text-text-primary"
        >
          {t(isSbp ? 'plans.sbpPayment.changeMethod' : 'plans.cryptoPayment.changeMethod')}
        </button>
      </div>
    </section>
  );
}
